import { useCallback, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import {
  AppointmentTableViewData,
  AtRiskStatus,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { ContactUserTableActions, UserCard, useHasAdminAccess, useScrollToTop } from '@frontend/analytics-shared';
import { Icon } from '@frontend/icons';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Chip, Table, TableColumnConfig, TableProps } from '@frontend/design-system';
import { APPOINTMENT_TABS } from '../../constants';
import { useAppointmentAnalyticsV2ShallowStore } from '../../hooks/use-appointment-analytics-v2-store';
import {
  AppointmentStatusChip,
  AppointmentConfirmationStatusChip,
  AppointmentNoShowRiskChip,
  PatientTypeChip,
} from '../atoms/appointment-status-chips';

const DEFAULT_PAGE_SIZE = 25;
const TABLE_ROWS_MAX_HEIGHT = 400;

export const AppointmentAnalyticsV2TableView = () => {
  const { t } = useTranslation('analytics');
  const { tableFilters, activeTab, filtersInitialized } = useAppointmentAnalyticsV2ShallowStore(
    'tableFilters',
    'activeTab',
    'filtersInitialized'
  );

  const [pageConfig, setPageConfig] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const hasAdminAccess = useHasAdminAccess();

  const activeFilters = tableFilters[activeTab] || {};

  const queryRequest: AnalyticsGatewayV1.Types.GetAppointmentsTableViewIO['input'] = {
    ...activeFilters,
    pageSize: pageConfig.pageSize,
  };

  const {
    data: appointmentsData,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = AnalyticsGatewayV1.Queries.useGetAppointmentsTableViewQuery(queryRequest, {
    enabled: filtersInitialized && !!activeFilters.locationId?.length,
  });

  // Scroll to top when component mounts
  useScrollToTop();

  // Reset to page 1 when filters change
  useEffect(() => {
    setPageConfig((prev) => ({ ...prev, pageNumber: 1 }));
  }, [tableFilters, activeTab, pageConfig.pageSize]);

  // Fetch next page when navigating forward to an unloaded page
  useEffect(() => {
    if (!appointmentsData?.pages[pageConfig.pageNumber - 1]) {
      fetchNextPage();
    }
  }, [appointmentsData?.pages, fetchNextPage, pageConfig.pageNumber]);

  const appointments = appointmentsData?.pages[pageConfig.pageNumber - 1]?.data ?? [];

  const getHasNext = useCallback(() => {
    const pageData = appointmentsData?.pages[pageConfig.pageNumber - 1]?.data ?? [];
    return hasNextPage && pageData.length === pageConfig.pageSize;
  }, [appointmentsData?.pages, hasNextPage, pageConfig.pageNumber, pageConfig.pageSize]);

  const colConfig: TableColumnConfig<AppointmentTableViewData>[] = [
    {
      id: 'contactName',
      Header: t('Contact & Appointment Date'),
      accessor: (item) => item,
      disableColumnFilter: true,
      cellRenderer: (value: any) => {
        return (
          <UserCard
            css={css`
              .time {
                font-size: ${theme.fontSize(12)};
                color: ${theme.colors.neutral50};
              }
            `}
            lastName={value?.patientName}
            key={value?.patientName}
            openProfileOnClick
            phoneNumber={''}
            showOnlyName
            time={value?.appointmentDate ? dayjs(value.appointmentDate).format('MMM D, YYYY') : undefined}
            userId={value?.patientId}
            locationId={value?.locationId}
          />
        );
      },
      sortType: (rowA, rowB) => {
        const nameA = rowA.original.patientName?.toLowerCase() || '';
        const nameB = rowB.original.patientName?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      },
      width: 250,
      minWidth: 250,
      sticky: 'left',
    },
    {
      id: 'location',
      Header: t('Location'),
      accessor: 'locationName',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return (
          <Chip
            leftElement={<Icon name='locations-small' />}
            maxWidth='100%'
            variant='neutral'
            css={{
              color: theme.colors.neutral70,
            }}
          >
            {value}
          </Chip>
        );
      },
    },
    {
      id: 'providerName',
      Header: t('Provider'),
      accessor: 'providerName',
      cellRenderer: (value: string) => (value ? value : '-'),
    },
    {
      id: 'contactType',
      Header: t('Contact Type'),
      accessor: 'contactType',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return <PatientTypeChip type={value} />;
      },
    },
    // Conditionally show status column based on Active Tab
    ...(activeTab === APPOINTMENT_TABS.PAST
      ? ([
          {
            id: 'status',
            Header: t('Appointment Status'),
            accessor: 'status',
            cellRenderer: (value: string) => {
              if (!value) return '-';
              return <AppointmentStatusChip status={value} />;
            },
          },
          {
            id: 'appointmentLeadTime',
            Header: t('Appointment Lead Time'),
            accessor: 'appointmentLeadTime',
            cellRenderer: (value: string) => (value ? value : '-'),
          },
          {
            id: 'cancellationLeadTime',
            Header: t('Cancellation Lead Time'),
            accessor: 'cancellationLeadTime',
            cellRenderer: (value: string) => (value ? value : '-'),
          },
        ] as TableColumnConfig<AppointmentTableViewData>[])
      : []),
    {
      id: 'confirmationStatus',
      Header: t('Confirmation Status'),
      accessor: 'confirmationStatus',
      cellRenderer: (value: string) => {
        if (!value) return '-';
        return <AppointmentConfirmationStatusChip status={value} />;
      },
    },
    {
      id: 'atRiskStatus',
      Header: t('No-Show Risk Status'),
      accessor: 'atRiskStatus',
      cellRenderer: (value: string) => {
        if (!value || value === AtRiskStatus.AT_RISK_STATUS_UNSPECIFIED) return '-';
        if (value === AtRiskStatus.AT_RISK_STATUS_REPEAT_BOTH)
          return (
            <div css={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <AppointmentNoShowRiskChip status={AtRiskStatus.AT_RISK_STATUS_REPEAT_CANCEL} />
              <AppointmentNoShowRiskChip status={AtRiskStatus.AT_RISK_STATUS_REPEAT_NSR} />
            </div>
          );
        return <AppointmentNoShowRiskChip status={value} />;
      },
    },
    {
      id: 'appointmentDate',
      Header: t('Appointment Date'),
      accessor: 'appointmentDate',
      cellRenderer: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
    },
    ...(activeTab === APPOINTMENT_TABS.PAST
      ? ([
          {
            id: 'createdAt',
            Header: t('Created At'),
            accessor: 'createdAt',
            cellRenderer: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
          },
        ] as TableColumnConfig<AppointmentTableViewData>[])
      : []),
    {
      Header: '',
      accessor: (item) => item,
      cellRenderer: (item: any) => {
        return (
          <ContactUserTableActions
            personId={item.patientId ?? ''}
            personName={item.patientName}
            phoneNumber={item.phoneNumber ?? ''}
          />
        );
      },
      disableSortBy: true,
      id: 'actions',
      sticky: 'right',
      disableColumnFilter: true,
      width: 'unset',
      minWidth: 108,
    },
  ];

  const tableProps: TableProps<any> = {
    isLoading: isLoading || isFetching,
    data: appointments,
    tableInstanceId: 'appointment-analytics-v2-table',
    hasFilterColumns: false,
    hasResizeColumns: true,
    colConfig,
    fullHeight: false,
    wrapperStyle: css`
      .table__body {
        max-height: ${TABLE_ROWS_MAX_HEIGHT}px;
      }
    `,
    styleConfig: {
      columns: [
        {
          id: 'actions',
          cellStyler: css`
            padding: 10px;
          `,
        },
      ],
    },
    isPaginated: true,
    manualPaginationConfig: {
      handleChange: (action: 'next' | 'prev') => {
        if (action === 'next') {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber + 1 }));
        } else {
          setPageConfig((prev) => ({ ...prev, pageNumber: prev.pageNumber - 1 }));
        }
      },
      hasNext: getHasNext(),
      hasPrevious: pageConfig.pageNumber > 1,
      page: pageConfig.pageNumber,
      defaultRowsPerPage: pageConfig.pageSize,
      onNumRowsChange: (num) => {
        setPageConfig({ pageNumber: 1, pageSize: num });
      },
      rowsPerPageOptions: [10, 25, 50, 75, 100],
    },
    customToolbarRender: hasAdminAccess ? CustomToolBar : undefined,
  };

  return <Table {...tableProps} />;
};

const CustomToolBar = () => {
  const { t } = useTranslation('analytics');

  const { navigate: settingsNavigate } = useSettingsNavigate();

  const handleViewReminderSettingsClick = () => {
    settingsNavigate({ to: '/messaging/auto-messages' });
  };

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(2),
      }}
    >
      <Button
        variant='tertiary'
        iconName='send'
        onClick={handleViewReminderSettingsClick}
        trackingId='view-reminder-settings-button'
      >
        {t('View Reminder Settings')}
      </Button>
    </div>
  );
};
